import type { CustomerDto } from '@tempo-io/fim3-dto';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemMultiCustomerValue } from '@/common/api/types/items';
import { useCustomersByIdsQueryWithPublished } from '@/features/item-panel/components/hooks/useCustomersByIdsQueryWithPublished';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import { CustomerFieldDisplay } from './CustomerFieldDisplay';
import { CustomerSelectionDialog } from './CustomerSelectionDialog';
import { MultiValueFieldManager, type MultiValueFieldValue } from './MultiValueFieldManager';
import { MULTI_VALUE_MAX_COUNT } from './validation';

interface MultiCustomerFieldProps {
  name: string;
  values?: ItemMultiCustomerValue[];
  isEditable?: boolean;
  autoFocus?: boolean;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  onChange: (customerIds: number[]) => void;
  onValidationChange?: (error: string | null) => void;
}

export const MultiCustomerField = ({
  name,
  values = [],
  isEditable = false,
  autoFocus = false,
  isPending = false,
  isSuccess = false,
  isError,
  error,
  onChange,
  onValidationChange,
}: MultiCustomerFieldProps) => {
  const { t } = useTranslation();
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(autoFocus && values.length === 0);

  const selectedIds = useMemo(() => values.map((v) => v.MULTI_CUSTOMER), [values]);
  const { data: hydration, isLoading: isHydrating } =
    useCustomersByIdsQueryWithPublished(selectedIds);

  const customersById = useMemo(() => {
    const map = new Map<number, CustomerDto>();
    for (const c of hydration?.data ?? []) {
      map.set(c.id, c);
    }
    return map;
  }, [hydration]);

  const handleValidationError = (err: string | null) => {
    setValidationError(err);
    onValidationChange?.(err);
  };

  const handleOnAdd = (customerId: number) => {
    if (selectedIds.includes(customerId)) return;
    if (values.length >= MULTI_VALUE_MAX_COUNT) {
      handleValidationError(
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiValueMaxCountReached, {
          maxCount: MULTI_VALUE_MAX_COUNT,
        }),
      );
      return;
    }
    handleValidationError(null);
    onChange([...selectedIds, customerId]);
  };

  const fieldValues: MultiValueFieldValue<ItemMultiCustomerValue>[] = values.map((v) => ({
    id: v.id,
    value: v,
  }));

  const resolvedIsError = !!validationError || !!isError;
  const resolvedError = validationError ? new Error(validationError) : (error ?? null);

  return (
    <FieldWithStatusIndicator name={name} isPending={isPending} isSuccess={isSuccess}>
      <div className="flex flex-col gap-1">
        <MultiValueFieldManager
          values={fieldValues}
          isEditable={isEditable}
          disabled={isPending}
          onRemove={(id) =>
            onChange(values.filter((v) => v.id !== id).map((v) => v.MULTI_CUSTOMER))
          }
          maxCount={MULTI_VALUE_MAX_COUNT}
          onAddClick={() => setDialogOpen(true)}
        >
          {(item) => (
            <CustomerFieldDisplay
              customerId={item.value.MULTI_CUSTOMER}
              customer={customersById.get(item.value.MULTI_CUSTOMER)}
              isLoading={isHydrating}
              isEditable={false}
            />
          )}
        </MultiValueFieldManager>
        {resolvedIsError && resolvedError && (
          <span className="text-xs text-text-error" role="alert">
            {resolvedError.message}
          </span>
        )}
      </div>

      <CustomerSelectionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        excludedCustomerIds={selectedIds}
        onSave={(customerId) => {
          handleOnAdd(customerId);
          setDialogOpen(false);
        }}
        title={t(($) => $.adaptivePlanner.itemPanel.multiCustomerField.addCustomer)}
      />
    </FieldWithStatusIndicator>
  );
};
